import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePortal } from "@/pages/admin/PortalLayout";

const GROUPS = ["Citizen", "Resident", "Investor", "Visitor", "Employee", "Government Staff", "VIP", "PORTAL_USER_MANAGER"];
const field = "h-11 w-full rounded-xl border border-input bg-card px-3 text-sm";

const AddUser = ({ groups, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", group: "Citizen", role: "PORTAL_USER" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/users", f); toast.success("User added"); setOpen(false); setF({ name: "", email: "", group: "Citizen", role: "PORTAL_USER" }); onDone(); }
    catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="users-add-btn" className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground"><Plus size={16} /> Add user</button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="users-form-name" required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} />
          <input data-testid="users-form-email" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} />
          <select data-testid="users-form-group" value={f.group} onChange={(e) => setF({ ...f, group: e.target.value, role: e.target.value === "PORTAL_USER_MANAGER" ? "PORTAL_USER_MANAGER" : "PORTAL_USER" })} className={field}>
            {[...new Set([...GROUPS, ...groups])].filter((g) => g !== "PLATFORM_SUPER_ADMIN").map((g) => <option key={g}>{g}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Role: <b>{f.role}</b></p>
          <button data-testid="users-form-submit" className="h-11 w-full rounded-xl bg-primary font-semibold text-primary-foreground">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const GroupsPanel = ({ groups, users, reload }) => {
  const [name, setName] = useState("");
  const [member, setMember] = useState({});
  const add = async (e) => { e.preventDefault(); try { await api.post("/portal/groups", { name }); setName(""); reload(); } catch (er) { toast.error(errMsg(er)); } };
  const addMember = async (g) => { try { await api.post(`/portal/groups/${encodeURIComponent(g)}/members`, { email: member[g] }); reload(); } catch (er) { toast.error(errMsg(er)); } };
  const remove = async (g, email) => { await api.delete(`/portal/groups/${encodeURIComponent(g)}/members/${encodeURIComponent(email)}`); reload(); };
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold md:text-lg">Groups</h2>
        <form onSubmit={add} className="flex gap-2">
          <input data-testid="groups-new-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="New group" className="h-10 rounded-xl border bg-card px-3 text-sm" />
          <button data-testid="groups-add-btn" className="h-10 rounded-xl border px-3 text-sm font-semibold">Add</button>
        </form>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {(groups || []).map((g) => (
          <div key={g.name} className="rounded-2xl border bg-card p-3 shadow-sm" data-testid={`group-card-${g.name}`}>
            <p className="text-sm font-bold">{g.name} <span className="text-xs font-normal text-muted-foreground">({g.members.length})</span></p>
            <div className="mt-2 flex flex-wrap gap-1">
              {g.members.map((m) => <span key={m} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px]">{m}<button aria-label={`Remove ${m}`} data-testid={`group-member-remove-${g.name}-${m}`} onClick={() => remove(g.name, m)}><X size={11} /></button></span>)}
            </div>
            <div className="mt-2 flex gap-1.5">
              <select value={member[g.name] || ""} onChange={(e) => setMember({ ...member, [g.name]: e.target.value })} className="h-9 flex-1 rounded-lg border bg-card px-2 text-xs" data-testid={`group-member-select-${g.name}`}>
                <option value="">Add member…</option>
                {users.filter((u) => !g.members.includes(u.email)).map((u) => <option key={u.email} value={u.email}>{u.email}</option>)}
              </select>
              <button disabled={!member[g.name]} onClick={() => addMember(g.name)} className="h-9 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-40" data-testid={`group-member-add-${g.name}`}>Add</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function PortalUsers() {
  const [users, reloadUsers] = usePortal("/portal/users");
  const [groups, reloadGroups] = usePortal("/portal/groups");
  const reload = () => { reloadUsers(); reloadGroups(); };
  const patch = async (email, body) => { try { await api.patch(`/portal/users/${encodeURIComponent(email)}`, body); reload(); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-8" data-testid="portal-users">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold md:text-3xl">User manager</h1><p className="text-sm text-muted-foreground">Add users, assign groups and roles.</p></div>
        <AddUser groups={(groups || []).map((g) => g.name)} onDone={reload} />
      </div>
      <div className="overflow-x-auto rounded-2xl border bg-card shadow-sm">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-3">ID</th><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Group</th><th className="p-3">Role</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {(users || []).map((u) => {
              const locked = u.role === "PLATFORM_SUPER_ADMIN";
              return (
                <tr key={u.email} className="border-t" data-testid={`user-row-${u.email}`}>
                  <td className="p-3 font-mono text-xs">{u.user_id}</td><td className="p-3 font-medium">{u.name}</td><td className="p-3 text-xs">{u.email}</td>
                  <td className="p-3">{locked ? u.group : (
                    <select value={u.group} onChange={(e) => patch(u.email, { group: e.target.value })} className="h-9 rounded-lg border bg-card px-2 text-xs" data-testid={`user-group-select-${u.email}`}>
                      {[...new Set([u.group, ...GROUPS])].map((g) => <option key={g}>{g}</option>)}
                    </select>)}</td>
                  <td className="p-3 text-xs font-semibold">{u.role}</td>
                  <td className="p-3">{locked ? <span className="text-xs">✓ Active (default admin)</span> : (
                    <button onClick={() => patch(u.email, { status: u.status === "Active" ? "Suspended" : "Active" })} data-testid={`user-status-toggle-${u.email}`}
                      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${u.status === "Active" ? "border-[#BADBCE] bg-[#D1E7DD] text-[#0F5132]" : "border-[#F5C2C7] bg-[#F8D7DA] text-[#842029]"}`}>
                      {u.status === "Active" ? "✓ Active" : "✕ Suspended"}
                    </button>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <GroupsPanel groups={groups} users={users || []} reload={reloadGroups} />
    </div>
  );
}
